import { createSharedComposable } from "@vueuse/core";

const API_URL = "https://maps-api.apple.com/v1";

// access tokens last 30 minutes; refresh a minute early
const TOKEN_EXPIRY_MARGIN_SECONDS = 60;

const _useMapkit = () => {
  let accessToken = null; // { value, expiresAt }
  let pendingToken = null;

  // `rejected`: a token the API refused, replaced unless another call already has
  async function getAccessToken(rejected) {
    if (accessToken && accessToken.expiresAt > Date.now() && accessToken.value !== rejected) {
      return accessToken.value;
    }
    pendingToken ??= $fetch(`${API_URL}/token`, {
      headers: { Authorization: `Bearer ${useRuntimeConfig().public.mapkitApiKey}` },
    })
      .then((data) => {
        if (!data?.accessToken) {
          throw new Error("Apple Maps did not return an access token");
        }
        const lifetime = (data.expiresInSeconds ?? 1800) - TOKEN_EXPIRY_MARGIN_SECONDS;
        accessToken = { value: data.accessToken, expiresAt: Date.now() + lifetime * 1000 };
      })
      .finally(() => {
        pendingToken = null;
      });
    await pendingToken;
    return accessToken.value;
  }

  async function appleFetch(path, params) {
    const request = (token) => $fetch(`${API_URL}${path}`, { headers: { Authorization: `Bearer ${token}` }, params });
    const token = await getAccessToken();
    try {
      return await request(token);
    } catch (error) {
      // the token may have expired early: get a new one and try once more
      if (error?.statusCode !== 401) {
        throw error;
      }
      return request(await getAccessToken(token));
    }
  }

  async function autocomplete(query, location) {
    const { latitude, longitude, lang = "en-US" } = location ?? {};
    const params = {
      q: query,
      lang,
      resultTypeFilter: "Poi,Address", // exclude `Query` results
    };
    if (isNumber(latitude) && isNumber(longitude)) {
      params.userLocation = `${latitude},${longitude}`;
    }
    const response = await appleFetch("/searchAutocomplete", params);
    return response?.results;
  }

  // The address at a point, in the shape of shared/utils/reverseGeocode.js. A server render (useInitialLocationData)
  // asks server/api/reverse-geocode, which signs its own tokens; the browser asks Apple with the MapKit JS key.
  // Coordinates are rounded as the server rounds them, so both answer the same for a point.
  async function reverseGeocode({ latitude, longitude, lang = "en-US" }) {
    const point = { latitude: round(latitude, 5), longitude: round(longitude, 5) };
    if (import.meta.server) {
      return $fetch("/api/reverse-geocode", { params: { ...point, lang } });
    }
    const response = await appleFetch("/reverseGeocode", { loc: `${point.latitude},${point.longitude}`, lang });
    return formatReverseGeocodeResult(response);
  }

  return {
    autocomplete,
    reverseGeocode,
  };
};

export const useMapkit = createSharedComposable(_useMapkit);
